import isEmpty from 'lodash/isEmpty';
import Head from 'next/head';
import { useRouter } from 'next/router';
import criticalCss from 'plaid-threads/utils/criticalCss'; // eslint-disable-line
import PropTypes from 'prop-types';
import { VWOScript } from 'vwo-smartcode-nextjs';

// components
import {
  AnalyticsScript,
  InitAnalytics,
} from 'src/contexts/AnalyticsContext/AnalyticsContext';
import BootLaunchDarkly from './BootLaunchDarkly';
import OpenGraphData from './OpenGraphData';

// helpers
import Script from 'next/script';
import { isProduction } from 'src/lib/constants';
import { optimizeImage } from 'src/lib/image-helpers';
import { getCanonicalUrl, getPagePath } from 'src/lib/url-helpers';

// TODO: are we preloading too many fonts?
const fonts = [
  'PlaidSans-Hairline.woff2',
  'PlaidSans-HairlineOblique.woff2',
  'PlaidSans-ExtraLight.woff2',
  'PlaidSans-ExtraLightOblique.woff2',
  'PlaidSans-Light.woff2',
  'PlaidSans-LightOblique.woff2',
  'PlaidSans-Regular.woff2',
  'PlaidSans-RegularOblique.woff2',
  'PlaidSans-Medium.woff2',
  'PlaidSans-MediumOblique.woff2',
  'PlaidSans-SemiBold.woff2',
  'PlaidSans-SemiBoldOblique.woff2',
  'PlaidSans-Bold.woff2',
  'PlaidSans-BoldOblique.woff2',
  'PlaidSans-ExtraBold.woff2',
  'PlaidSans-ExtraBoldOblique.woff2',
  'PlaidSans-Black.woff2',
  'PlaidSans-BlackOblique.woff2',
  'cern-regular.woff2',
  'cern-italic.woff2',
  'cern-medium.woff2',
  'cern-mediumitalic.woff2',
  'cern-semibold.woff2',
  'cern-semibolditalic.woff2',
  'cern-bold.woff2',
  'cern-bolditalic.woff2',
  'cern-extrabold.woff2',
  'cern-extrabolditalic.woff2',
  'Inconsolata-Regular.woff2',
  'Inconsolata-Bold.woff2',
  'BoweryStreet-Light.woff2',
  'BoweryStreet-Regular.woff2',
  'BoweryStreet-Medium.woff2',
  'BoweryStreet-Bold.woff2',
];

const propTypes = {
  'meta-title': PropTypes.string.isRequired,
  'meta-description': PropTypes.string.isRequired,
  excludeBrandFromMetaTitle: PropTypes.bool,
  hasCanonicalTag: PropTypes.bool,
  canonicalPagePath: PropTypes.string,
  noIndex: PropTypes.bool,
  robotsContent: PropTypes.string,
  preloadImages: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      media: PropTypes.string,
    }),
  ),
  children: PropTypes.node,
  structuredData: PropTypes.object,
};

/**
 * Returns `${metaTitle} | Plaid` unless excludeBrand is true
 *
 * @param {Object} args
 * @param {string} args.metaTitle
 * @param {boolean} args.excludeBrand - removes branding when true
 * @returns {string}
 */
const makeMetaTitle = ({ metaTitle = '', excludeBrand = false }) => {
  if (metaTitle.includes('| Plaid')) {
    return metaTitle;
  }
  if (excludeBrand) {
    return metaTitle;
  }
  return `${metaTitle} | Plaid`;
};

const MetaTags = ({
  'meta-description': metaDescription,
  excludeBrandFromMetaTitle: excludeBrand = false,
  hasCanonicalTag = true,
  canonicalPagePath,
  noIndex,
  robotsContent,
  preloadImages = [],
  children,
  structuredData = {},
  ...props
}) => {
  const { asPath } = useRouter();
  const metaTitle = makeMetaTitle({
    metaTitle: props['meta-title'],
    excludeBrand,
  });
  const pagePath = getPagePath(asPath);
  const canonicalUrl = getCanonicalUrl({ canonicalPagePath, pagePath });

  return (
    <>
      <Head>
        <BootLaunchDarkly />

        <meta charSet='utf-8' />
        <meta httpEquiv='x-ua-compatible' content='ie=edge' />
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
        <meta name='msapplication-TileColor' content='#da532c' />
        <meta name='theme-color' content='#ffffff' />
        {(robotsContent || noIndex) && (
          <meta name='robots' content={robotsContent || 'noindex, nofollow'} />
        )}

        {/*
          Prevent iOS from detecting phone numbers and wrapping them in `<a>` tags
          @see https://developer.apple.com/library/archive/documentation/AppleApplications/Reference/SafariHTMLRef/Articles/MetaTags.html
        */}
        <meta name='format-detection' content='telephone=no' />

        {hasCanonicalTag && <link rel='canonical' href={canonicalUrl} />}
        <link
          rel='preconnect'
          href='https://images.ctfassets.net'
          crossOrigin='anonymous'
        />
        <link rel='dns-prefetch' href='https://images.ctfassets.net' />
        <link
          rel='preconnect'
          href='https://assets.ctfassets.net'
          crossOrigin='anonymous'
        />
        <link rel='dns-prefetch' href='https://assets.ctfassets.net' />
        <link
          rel='preconnect'
          href='https://videos.ctfassets.net'
          crossOrigin='anonymous'
        />
        <link rel='dns-prefetch' href='https://videos.ctfassets.net' />
        <link
          rel='apple-touch-icon'
          sizes='180x180'
          href='/assets/img/favicons/apple-touch-icon.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='32x32'
          href='/assets/img/favicons/favicon-32x32.png'
        />
        <link
          rel='icon'
          type='image/png'
          sizes='16x16'
          href='/assets/img/favicons/favicon-16x16.png'
        />
        <link rel='manifest' href='/assets/img/favicons/site.webmanifest' />
        <link
          rel='mask-icon'
          href={'/assets/img/favicons/safari-pinned-tab.svg'}
          color='#5bbad5'
        />
        {preloadImages?.map((image, i) => {
          return (
            <link
              as='image'
              fetchpriority='high'
              href={optimizeImage(image.src)}
              key={`image-${i}`}
              rel='preload'
              type={image.type || 'image/webp'}
              {...(image?.media && { media: image.media })}
              {...(image?.imagesrcset && { imagesrcset: image.imagesrcset })}
              {...(image?.imagesizes && { imagesizes: image.imagesizes })}
            />
          );
        })}

        {fonts.map((font) => {
          return (
            <link
              key={font}
              rel='preload'
              as='font'
              href={`/assets/fonts/${font}`}
              crossOrigin='anonymous'
            />
          );
        })}

        <title>{metaTitle}</title>
        {metaDescription && (
          <meta name='description' content={metaDescription} />
        )}
        {OpenGraphData({ metaTitle, metaDescription, pagePath, ...props })}
        <meta
          name='google-site-verification'
          content='92umdhJ1LufsliBdSAFEltp1osgG_ehru4C__nMCJpQ'
        />
        <meta
          name='facebook-domain-verification'
          content='8pfbsxqbxm99hpn4wd4lmfewlc624z'
        />

        <style
          type='text/css'
          dangerouslySetInnerHTML={{
            __html: criticalCss([
              'Touchable',
              'Icon',
              'Button',
              'Grid',
              'Column',
              'Input',
              'Select',
              'Banner',
            ]),
          }}
        />

        <AnalyticsScript />

        {!isEmpty(structuredData) && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        )}

        {/**
         * Note: VWO automatically integrates with OneTrust consent preferences, so we don't need to do anything further here
         * @see https://help.vwo.com/hc/en-us/articles/34254349279257-Configuring-VWO-SmartCode-for-Your-Website-s-Cookie-Consent
         */}
        <VWOScript accountId={'1200187'} />

        {children}
      </Head>

      <InitAnalytics />

      {isProduction && <Script src='/assets/js/hire.js' />}
    </>
  );
};

MetaTags.propTypes = propTypes;

export default MetaTags;
